import React, { useState, useMemo, useRef } from "react";
import { copyText } from "@/lib/clipboard";
import { useQuery } from "@tanstack/react-query";
import { Trash2, Copy, Plus, RefreshCw, CheckCircle2, XCircle, Pencil, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth, hasPerm } from "@/context/AuthContext";
import { useResourceList, useResourceMutations } from "@/hooks/useResource";
import { useOptions } from "@/hooks/useOptions";
import { PageHeader } from "@/components/kit/PageHeader";
import { DataTablePro } from "@/components/kit/DataTablePro";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { IdCell } from "@/components/kit/IdCell";
import { Button } from "@/components/ui/button";
import { FormDrawer } from "@/components/kit/FormDrawer";
import { TextField, SelectField, Field } from "@/components/kit/fields";
import { SearchSelect } from "@/components/kit/SearchSelect";
import { MultiSelect } from "@/components/kit/MultiSelect";
import { DateRangePicker } from "@/components/kit/DateRangePicker";
import { fmtDate, fmtDateTime, shortId } from "@/lib/format";

const asOpts = (arr) => (arr || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
const fmtOffer = (o) => `#${o.tenant_seq} ${o.name}${o.geo ? ` – [${String(o.geo).toUpperCase()}]` : ""}`;

const TOKENS = [
  { t: "{sub_id_1} … {sub_id_9}", d: "Click sub params (e.g. telegram_id)" },
  { t: "{status}", d: "lead · sale · redep · revshare · trash" },
  { t: "{event}", d: "Payment status: hold · approved · trash" },
  { t: "{payout}", d: "Payout amount" },
  { t: "{currency}", d: "Currency code" },
  { t: "{offer_name}", d: "Offer / GEO offer name" },
  { t: "{campaign_name}", d: "Stream / campaign name" },
  { t: "{order_id}", d: "Conversion order ID" },
  { t: "{country}", d: "Offer GEO country code" },
  { t: "{ip}", d: "Click IP address" },
];

// Macros the user may place in their postback URL values. Internal platform
// identifiers (system click_id / subid) are intentionally excluded — those are
// technical IDs for inbound advertiser integration only, not outbound macros.
const AVAILABLE = [
  "sub_id", "sub_id_1", "sub_id_2", "sub_id_3", "sub_id_4", "sub_id_5", "sub_id_6", "sub_id_7", "sub_id_8", "sub_id_9",
  "status", "event", "payout", "currency", "offer_name", "campaign_name", "campaign", "stream", "stream_name",
  "source", "partner", "partner_id", "affiliate_id", "order_id", "orderid", "offer", "offer_id",
  "country", "geo", "ip",
];

// Renders a URL string with {macros} highlighted inline. Known macros are green,
// unknown ones are dimmed/amber so partners can spot typos at a glance.
function HighlightUrl({ url, testId, subidAllowed = true, sourceAllowed = true }) {
  const parts = useMemo(() => {
    if (!url) return [];
    return url.split(/(\{[a-zA-Z0-9_]+\})/g).filter((s) => s !== "");
  }, [url]);
  if (!url) return null;
  return (
    <code data-testid={testId} className="block break-all font-mono text-xs leading-relaxed text-muted-foreground/80">
      {parts.map((part, i) => {
        const m = part.match(/^\{([a-zA-Z0-9_]+)\}$/);
        if (!m) return <span key={i}>{part}</span>;
        const blocked = (m[1] === "sub_id" && !subidAllowed) || (m[1] === "source" && !sourceAllowed);
        const ok = AVAILABLE.includes(m[1]) && !blocked;
        const cls = blocked
          ? "bg-[#EF4444]/15 text-[#EF4444]"
          : ok
          ? "bg-[#22C55E]/15 text-[#22C55E]"
          : "bg-[#F59E0B]/15 text-[#F59E0B]";
        return (
          <span key={i} className={`rounded px-1 py-0.5 font-semibold ${cls}`}>
            {part}
          </span>
        );
      })}
    </code>
  );
}

function Analyser({ url, subidAllowed = true, sourceAllowed = true }) {
  const tokens = useMemo(() => {
    if (!url || !url.includes("?")) return [];
    const qs = url.split("?").slice(1).join("?");
    const found = [];
    // Inspect only parameter VALUES (after '='); partner param names are arbitrary.
    qs.split("&").filter(Boolean).forEach((p) => {
      const eq = p.indexOf("=");
      const value = eq === -1 ? "" : p.slice(eq + 1);
      (value.match(/\{([a-zA-Z0-9_]+)\}/g) || []).forEach((tok) => {
        const name = tok.replace(/[{}]/g, "");
        if (!found.some((f) => f.name === name)) {
          const blocked = (name === "sub_id" && !subidAllowed) || (name === "source" && !sourceAllowed);
          found.push({ name, token: tok, ok: AVAILABLE.includes(name) && !blocked, blocked });
        }
      });
    });
    return found;
  }, [url, subidAllowed, sourceAllowed]);

  if (!url) return null;
  const blocked = tokens.filter((t) => t.blocked);
  const unknown = tokens.filter((t) => !t.ok && !t.blocked);
  return (
    <div className="space-y-2.5 rounded-lg border border-white/[0.1] bg-[#0A0A0A] px-3 py-3" data-testid="postback-analyse">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70">Live preview</p>
        <button type="button" onClick={() => copyText(url)} data-testid="analyse-copy-url"
          className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground">
          <Copy className="h-3 w-3" /> Copy full URL
        </button>
      </div>
      <HighlightUrl url={url} testId="analyse-preview-url" subidAllowed={subidAllowed} sourceAllowed={sourceAllowed} />
      {blocked.length > 0 && (
        <p data-testid="analyse-subid-blocked" className="rounded-md bg-[#EF4444]/10 px-2 py-1.5 text-[11px] text-[#EF4444]">
          Your account is not allowed to use {blocked.map((b) => `{${b.name}}`).join(", ")}. Remove {blocked.map((b) => `{${b.name}}`).join(", ")} from the Postback URL.
        </p>
      )}
      {tokens.length === 0 ? (
        <p className="text-xs text-muted-foreground">No platform macros detected. Put macros like <code className="text-[#22C55E]">{"{status}"}</code> <code className="text-[#22C55E]">{"{payout}"}</code> inside your parameter values.</p>
      ) : (
        <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
          <span className="text-muted-foreground/70">{tokens.length} macro{tokens.length > 1 ? "s" : ""} detected</span>
          {unknown.length > 0 && (
            <span data-testid="analyse-unknown" className="rounded-md bg-[#F59E0B]/15 px-2 py-0.5 font-mono text-[#F59E0B]">
              {unknown.length} unknown: {unknown.map((t) => t.token).join(" ")}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function PostbackLogs({ postbackId }) {
  const [openPanel, setOpenPanel] = useState(false);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["postback-logs", postbackId],
    queryFn: async () => (await http.get(`/postbacks/${postbackId}/logs`)).data,
    enabled: !!postbackId && openPanel,
  });
  const replay = async (logId) => {
    try { await http.post(`/postbacks/${postbackId}/logs/${logId}/replay`); toast.success("Replayed"); refetch(); }
    catch { toast.error("Replay failed"); }
  };
  const logs = data?.items || [];
  const logFilterFn = (l, f) => {
    if (f.dateFrom && (l.created_at || "") < f.dateFrom) return false;
    if (f.dateTo && (l.created_at || "") > f.dateTo + "T23:59:59") return false;
    return true;
  };
  const columns = [
    { key: "ok", label: "", render: (l) => l.success ? <CheckCircle2 className="h-4 w-4 text-[#22C55E]" /> : <XCircle className="h-4 w-4 text-[#EF4444]" /> },
    { key: "http_status", label: "HTTP", mono: true, render: (l) => <span className="font-mono">{l.method} · {l.http_status || "ERR"}</span> },
    { key: "response_ms", label: "Time", mono: true, render: (l) => <span className="text-muted-foreground">{l.response_ms}ms</span> },
    { key: "event", label: "Event", render: (l) => l.event ? <StatusBadge status={l.event} /> : "—" },
    { key: "url", label: "URL", render: (l) => <IdCell value={l.url} testId="log-url" maxWidth={360} /> },
    { key: "created_at", label: "Sent", sortable: true, render: (l) => fmtDateTime(l.created_at) },
  ];
  const rowActions = (l) => [{ label: "Replay", icon: RefreshCw, onClick: () => replay(l.id), testId: `replay-${l.id}` }];

  return (
    <div className="mt-2 rounded-xl border border-white/[0.08] bg-[#0E0E10]" data-testid="postback-logs-panel">
      <button type="button" onClick={() => setOpenPanel((o) => !o)} data-testid="toggle-delivery-logs"
        className="flex w-full items-center gap-2 px-4 py-3 text-sm font-medium text-foreground">
        {openPanel ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        Delivery Logs {logs.length ? <span className="text-muted-foreground">({logs.length})</span> : null}
      </button>
      {openPanel && (
        <div className="border-t border-white/[0.06] p-3">
          {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : (
            <DataTablePro testId="delivery-logs-table" columns={columns} rows={logs}
              searchKeys={["url", "http_status", "event", "method"]} searchPlaceholder="Search delivery logs..." exportName="delivery-logs"
              rowActions={rowActions} filterFn={logFilterFn}
              filters={[{ key: "success", label: "Result", getValue: (l) => (l.success ? "ok" : "failed"), options: [{ value: "ok", label: "Success" }, { value: "failed", label: "Failed" }] }]}
              customFilters={({ values, set }) => (
                <div className="w-full sm:w-[190px]">
                  <DateRangePicker from={values.dateFrom} to={values.dateTo}
                    onChange={({ from, to }) => { set("dateFrom", from); set("dateTo", to); }} placeholder="Date range" testId="logs-date-range" />
                </div>
              )}
              emptyText="No deliveries yet. Logs appear after a conversion fires this postback." />
          )}
        </div>
      )}
    </div>
  );
}

export default function Postbacks() {
  const { user } = useAuth();
  const opts = useOptions();
  const isBuyer = user.role === "buyer";
  const staffAlways = ["tenant_admin", "manager"].includes(user.role) || (user.permissions || []).includes("*");
  const subidAllowed = staffAlways || !!user.allow_subid_postback;
  const sourceAllowed = staffAlways || !!user.allow_source_postback;
  const { data, isLoading } = useResourceList("postbacks", {});
  const { data: offersData } = useResourceList("offers", {});
  const { data: streamsData } = useResourceList("streams", {});
  const { create, update, remove } = useResourceMutations("postbacks");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { name: "", url: "", scope: "global", offer_id: "", stream_ids: [], event: "sale", method: "GET", status: "active" };
  const [form, setForm] = useState(empty);
  const baseline = useRef(JSON.stringify(empty));

  const openNew = () => { setEditing(null); setForm(empty); baseline.current = JSON.stringify(empty); setOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    const init = { name: row.name || "", url: row.url || "", scope: row.scope || "global", offer_id: row.offer_id || "", stream_ids: row.stream_ids || [], event: row.event || "sale", method: row.method || "GET", status: row.status || "active" };
    setForm(init);
    baseline.current = JSON.stringify(init);
    setOpen(true);
  };
  const dirty = JSON.stringify(form) !== baseline.current;

  const offers = useMemo(() => offersData?.items || [], [offersData]);
  const offerName = (id) => { const o = offers.find((x) => x.id === id); return o ? fmtOffer(o) : (id ? shortId(id) : "—"); };
  const ownerMap = useMemo(() => {
    const m = {};
    [...(opts.buyers || []), ...(opts.managers || []), ...(opts.responsibles || [])].forEach((u) => { m[u.id] = u.name; });
    return m;
  }, [opts]);
  const offerOptions = useMemo(() => offers.map((o) => ({
    value: o.id, label: fmtOffer(o),
    search: `#${o.tenant_seq} ${o.tenant_seq} ${o.name || ""} ${o.geo || ""} ${o.advertiser_name || ""} ${o.product_name || ""}`,
  })), [offers]);
  // Only ACTIVE streams owned by the acting user — a postback fires for its owner's traffic only.
  const streams = useMemo(() => streamsData?.items || [], [streamsData]);
  const streamOptions = useMemo(() => streams
    .filter((s) => s.buyer_id === user.id && (s.status || "active") !== "paused")
    .map((s) => ({ value: s.id, label: `#${s.tenant_seq ?? ""} ${s.name || s.stream_key}`.trim(), search: `#${s.tenant_seq ?? ""} ${s.tenant_seq ?? ""} ${s.name || ""} ${s.stream_key || ""} ${s.offer_name || ""}` })), [streams, user.id]);
  const streamName = (id) => { const s = streams.find((x) => x.id === id); return s ? (s.name || s.stream_key) : shortId(id); };

  const save = async () => {
    if (!form.name || !form.url) return toast.error("Name and URL are required");
    if (!/^https?:\/\//i.test(form.url.trim())) return toast.error("URL must start with http:// or https://");
    if (form.scope === "specific" && !form.stream_ids.length) return toast.error("Select at least one stream for this scope");
    if (!subidAllowed && /\{sub_id\}/.test(form.url)) {
      return toast.error("You don't have permission to use {sub_id} in outgoing postbacks. Remove {sub_id} from the Postback URL.");
    }
    if (!sourceAllowed && /\{source\}/.test(form.url)) {
      return toast.error("You don't have permission to use {source} in outgoing postbacks. Remove {source} from the Postback URL.");
    }
    const body = { name: form.name, url: form.url, scope: form.scope, event: form.event, method: form.method, status: form.status,
      stream_ids: form.scope === "specific" ? form.stream_ids : [], offer_id: null };
    if (editing) await update.mutateAsync({ id: editing.id, body });
    else await create.mutateAsync(body);
    setForm(empty);
    setEditing(null);
    setOpen(false);
  };

  const columns = [
    { key: "id", label: "ID", mono: true, render: (r) => <span className="text-muted-foreground">{shortId(r.id)}</span> },
    { key: "name", label: "Name", sortable: true, render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
    ...(!isBuyer ? [{ key: "owner", label: "Owner", exportValue: (r) => ownerMap[r.user_id] || shortId(r.user_id), render: (r) => <span className="text-sm text-muted-foreground">{ownerMap[r.user_id] || shortId(r.user_id)}</span> }] : []),
    { key: "scope", label: "Scope", render: (r) => r.scope === "specific"
      ? (r.stream_ids?.length
        ? <span className="text-sm text-foreground/90" title={r.stream_ids.map(streamName).join(", ")}>{r.stream_ids.length === 1 ? streamName(r.stream_ids[0]) : `${r.stream_ids.length} streams`}</span>
        : <span className="text-sm text-foreground/90">{offerName(r.offer_id)}</span>)
      : <span className="rounded-md bg-white/[0.05] px-2 py-0.5 text-xs text-muted-foreground">Global</span> },
    { key: "event", label: "Event", sortable: true, render: (r) => <StatusBadge status={r.event} /> },
    { key: "method", label: "Method", mono: true },
    { key: "url", label: "URL", render: (r) => <IdCell value={r.url} testId="pb-url" maxWidth={360} /> },
    { key: "status", label: "Status", sortable: true, render: (r) => <StatusBadge status={r.status} /> },
    { key: "created_at", label: "Created", sortable: true, render: (r) => fmtDate(r.created_at) },
  ];

  const filters = [
    { key: "event", label: "Event", getValue: (r) => r.event, options: asOpts(["lead", "sale", "redep", "revshare", "trash"]) },
    { key: "scope", label: "Scope", getValue: (r) => r.scope || "global", options: asOpts([{ value: "global", label: "Global" }, { value: "specific", label: "Specific streams" }]) },
    { key: "status", label: "Status", getValue: (r) => r.status, options: asOpts(["active", "disabled"]) },
  ];

  const rowActions = (row) => [
    ...(hasPerm(user, "postbacks.create") ? [{ label: "Edit", icon: Pencil, onClick: () => openEdit(row), testId: `edit-postback-${row.id}` }] : []),
    { label: "Copy URL", icon: Copy, onClick: () => copyText(row.url), testId: `copy-postback-${row.id}` },
    ...(hasPerm(user, "postbacks.create") ? [{ label: "Delete", icon: Trash2, danger: true, onClick: () => window.confirm("Delete postback?") && remove.mutate(row.id), testId: `delete-postback-${row.id}` }] : []),
  ];

  return (
    <div className="space-y-5">
      <PageHeader title="Postbacks" subtitle="Your personal server-to-server postbacks. Keitaro-compatible tokens." />

      <DataTablePro testId="postbacks-table" columns={columns} rows={data?.items || []} loading={isLoading}
        filters={filters} searchKeys={["name", "url"]} searchPlaceholder="Search postbacks..." exportName="postbacks"
        rowActions={rowActions} onRowClick={hasPerm(user, "postbacks.create") ? (r) => openEdit(r) : undefined}
        emptyText="No postbacks yet. Add one to forward conversions to your tracker."
        toolbarRight={hasPerm(user, "postbacks.create") && (
          <Button onClick={openNew} data-testid="add-postback-btn" className="h-9 gap-1.5 bg-white text-black hover:bg-white/90"><Plus className="h-4 w-4" /> Add Postback</Button>
        )} />

      <FormDrawer open={open} onOpenChange={setOpen} title={editing ? "Edit Postback" : "New Postback"} subtitle="Forward conversions to your own tracker / CRM" size="md" dirty={dirty}
        onSave={save} saving={create.isPending || update.isPending} saveLabel={editing ? "Save changes" : "Create"} testId="postback-drawer">
        <div className="space-y-5">
          <TextField label="Name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} testId="postback-name" placeholder="e.g. Keitaro main" />
          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground">Postback URL</label>
            <textarea value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} data-testid="postback-url" rows={3}
              placeholder="https://your-tracker.io/postback?cid={sub_id_1}&status={status}&payout={payout}&camp={campaign_name}"
              className="w-full resize-y rounded-lg border border-white/[0.12] bg-[#0A0A0A] px-3 py-2 font-mono text-xs text-foreground focus:border-white/30 focus:outline-none" />
            <Analyser url={form.url} subidAllowed={subidAllowed} sourceAllowed={sourceAllowed} />
            <div className="rounded-lg border border-white/[0.08] bg-[#0A0A0A] px-3 py-2.5">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70">Available macros</p>
              <div className="grid gap-x-5 gap-y-1.5 sm:grid-cols-2">
                {[...TOKENS, ...(sourceAllowed ? [{ t: "{source}", d: "Partner (affiliate) ID" }] : [])].map((tk) => (
                  <div key={tk.t} className="flex items-center justify-between gap-2 whitespace-nowrap text-xs">
                    <code className="shrink-0 text-[#22C55E]">{tk.t}</code>
                    <span className="truncate text-right text-muted-foreground/70" title={tk.d}>{tk.d}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <SelectField label="Scope" value={form.scope} onChange={(v) => setForm((f) => ({ ...f, scope: v }))} options={[{ value: "global", label: "Global (all streams)" }, { value: "specific", label: "Specific streams" }]} testId="postback-scope" />
            <SelectField label="Event / status" value={form.event} onChange={(v) => setForm((f) => ({ ...f, event: v }))} options={["sale", "lead", "redep", "revshare", "trash"]} testId="postback-event" />
          </div>
          {form.scope === "specific" && (
            <Field label="Streams" hint="This postback fires only for conversions on the selected streams. Deleted streams are detached automatically.">
              <MultiSelect values={form.stream_ids} onChange={(v) => setForm((f) => ({ ...f, stream_ids: v }))} options={streamOptions}
                label="Select streams" searchPlaceholder="Search by #ID, name, key..." testId="postback-streams" widthClass="w-[360px]" />
            </Field>
          )}
          <div className="grid grid-cols-2 gap-4">
            <SelectField label="Method" value={form.method} onChange={(v) => setForm((f) => ({ ...f, method: v }))} options={["GET", "POST"]} testId="postback-method" />
            <SelectField label="Status" value={form.status} onChange={(v) => setForm((f) => ({ ...f, status: v }))} options={["active", "disabled"]} testId="postback-status" />
          </div>
          {editing && <PostbackLogs postbackId={editing.id} />}
        </div>
      </FormDrawer>
    </div>
  );
}
